import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const isSupported =
  typeof window !== 'undefined' &&
  'IntersectionObserver' in window &&
  'IntersectionObserverEntry' in window &&
  'intersectionRatio' in window.IntersectionObserverEntry.prototype &&
  'isIntersecting' in window.IntersectionObserverEntry.prototype;

export const useIsInViewport = (
  elementId: string | undefined,
  observerConfig?: IntersectionObserverInit
) => {
  const [isVisible, setIsVisible] = useState(!isSupported);
  const [isShownAtLeastOnce, setIsShownAtLeastOnce] = useState(!isSupported);

  const observerCallback = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    (entries: IntersectionObserverEntry[], observer: IntersectionObserver) => {
      entries.forEach((entry) => {
        setIsVisible(entry.isIntersecting);
      });
    },
    []
  );

  const observerRef = useRef(
    isSupported
      ? new IntersectionObserver(observerCallback, {
          threshold: 0,
          rootMargin: '40%',
          ...observerConfig
        })
      : undefined
  );

  useEffect(() => {
    const observer = observerRef.current;
    if (!observer) {
      return;
    }
    if (elementId) {
      const el = document.getElementById(elementId);
      if (el) {
        observer.observe(el);
      }
    }

    return () => {
      observer?.disconnect();
    };
  }, [elementId]);

  if (isVisible && !isShownAtLeastOnce) {
    setIsShownAtLeastOnce(true);
  }

  return useMemo(
    () => ({ isVisible, isShownAtLeastOnce }),
    [isVisible, isShownAtLeastOnce]
  );
};
